import { useState, useRef, useEffect } from "react";

interface Props {
  isRecording: boolean;
  arming?: boolean;
  ending?: boolean;
  paused?: boolean;
  vadMode: "manual" | "auto";
  /** Hide the Manual/Auto switch (mobile: AUTO is disabled — loudspeaker echo
   *  makes silence-detection turn-taking unreliable there). */
  allowAuto?: boolean;
  onToggleVadMode: () => void;
  onToggleMic: () => void;
  onSendText: (text: string) => void;
  onTogglePause: () => void;
  onEnd: () => void;
}

function MicIcon({ size = 24 }: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 4.5v15a1 1 0 0 0 1.5.87l13-7.5a1 1 0 0 0 0-1.74l-13-7.5A1 1 0 0 0 7 4.5z" />
    </svg>
  );
}

function KeyboardIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M6 9h.01M10 9h.01M14 9h.01M18 9h.01M6 13h.01M18 13h.01M9 13h6" />
      <path d="M8 16.5h8" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
    </svg>
  );
}

export default function AudioControls({ isRecording, arming = false, ending = false, paused = false, vadMode, allowAuto = true, onToggleVadMode, onToggleMic, onSendText, onTogglePause, onEnd }: Props) {
  const [text, setText] = useState("");
  const [textMode, setTextMode] = useState(false);
  const [endArmed, setEndArmed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const desktopInputRef = useRef<HTMLInputElement>(null);
  const endResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // While paused, every input but Resume is frozen.
  const locked = ending || paused;

  // Pop the keyboard as soon as text mode opens — the user just asked to type.
  useEffect(() => {
    if (textMode) inputRef.current?.focus();
  }, [textMode]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSendText(trimmed);
    setText("");
    (document.activeElement === desktopInputRef.current ? desktopInputRef : inputRef).current?.focus();
  };

  const handleEndTap = () => {
    if (endArmed) {
      if (endResetRef.current) clearTimeout(endResetRef.current);
      setEndArmed(false);
      onEnd();
    } else {
      setEndArmed(true);
      endResetRef.current = setTimeout(() => setEndArmed(false), 3000);
    }
  };

  const status: { title: string; detail?: string } = ending
    ? { title: "Ending session…" }
    : paused
      ? { title: "Paused", detail: "Tap resume when you're ready" }
      : endArmed
        ? { title: "End session?", detail: "Tap the red button again to confirm" }
        : arming
          ? { title: "Opening the mic…" }
          : isRecording
          ? vadMode === "auto"
            ? { title: "Elena is listening", detail: "Stay quiet to finish your turn" }
            : { title: "Your turn", detail: "Tap ✓ when you're done" }
          : textMode
            ? { title: "Text mode", detail: "Scrivi in italiano or go back to voice" }
            : vadMode === "auto"
              ? { title: "Elena is speaking", detail: "Your turn starts automatically" }
              : { title: "Elena is speaking", detail: "Tap the mic to speak" };

  return (
    <div className="flex-shrink-0 border-t border-border-soft bg-surface/70 backdrop-blur-sm pb-[max(env(safe-area-inset-bottom)-14px,0px)]">
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-8 pt-3 pb-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-300 ${
                ending || paused || arming ? "bg-warning animate-pulse-soft" : endArmed ? "bg-error animate-pulse-soft" : isRecording ? "bg-accent animate-pulse-soft" : "bg-success"
              }`}
            />
            <span className="flex flex-col min-w-0 leading-snug">
              <span className="text-[0.5625rem] sm:text-[0.6875rem] uppercase tracking-[0.08em] sm:tracking-[0.14em] text-text-tertiary font-medium">{status.title}</span>
              {status.detail && <span className="text-[0.6875rem] sm:text-xs text-text-tertiary/80">{status.detail}</span>}
            </span>
          </div>
          {allowAuto && (
            <button
              onClick={onToggleVadMode}
              disabled={locked}
              className="flex items-center rounded-full border border-border-soft overflow-hidden text-[0.625rem] font-medium uppercase tracking-wider flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Switch between manual and auto voice detection"
            >
              <span className={`px-2 sm:px-2.5 py-1 transition-colors ${vadMode === "manual" ? "bg-ink text-cream" : "text-text-tertiary"}`}>Manual</span>
              <span className={`px-2 sm:px-2.5 py-1 transition-colors ${vadMode === "auto" ? "bg-ink text-cream" : "text-text-tertiary"}`}>Auto</span>
            </button>
          )}
        </div>

        <div className="md:hidden">
        {textMode ? (
          <div className="flex items-center gap-2 sm:gap-2.5 min-h-[4rem]">
            <button
              className="w-11 h-11 rounded-full bg-ink text-cream flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer hover:bg-[var(--ink-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => setTextMode(false)}
              disabled={ending}
              title="Back to voice"
              aria-label="Back to voice"
            >
              <MicIcon size={18} />
            </button>

            <input
              ref={inputRef}
              className="input-base flex-1 min-w-0 rounded-full px-4 h-11"
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
              disabled={locked}
              placeholder={paused ? "Paused" : "Scrivi in italiano…"}
            />

            <button
              className="w-11 h-11 rounded-full bg-ink text-cream flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer hover:bg-[var(--ink-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={handleSend}
              disabled={!text.trim() || locked}
              aria-label="Send message"
            >
              <ArrowUpIcon />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-4 min-h-[4rem]">
            <div className="flex items-center justify-start gap-2.5 sm:gap-3">
              <button
                className="w-11 h-11 rounded-full border border-border-soft text-text-secondary flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer hover:text-text-primary hover:border-border disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => setTextMode(true)}
                disabled={locked || isRecording}
                title="Type instead of speaking"
                aria-label="Type instead of speaking"
              >
                <KeyboardIcon />
              </button>
            </div>

            <button
              className={`relative w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                isRecording
                  ? "bg-accent text-cream shadow-[0_0_0_6px_var(--accent-soft)]"
                  : arming
                    ? "bg-warning text-cream animate-pulse-soft"
                    : "bg-ink text-cream hover:bg-[var(--ink-hover)]"
              }`}
              onClick={onToggleMic}
              disabled={locked}
              title={isRecording ? "Tap when you're done speaking" : arming ? "Opening the mic…" : "Tap to speak"}
              aria-label={isRecording ? "Finish your turn" : arming ? "Opening the mic" : "Start speaking"}
            >
              {isRecording && <span className="absolute -inset-1.5 rounded-full border border-accent/50 animate-pulse-soft" aria-hidden />}
              {arming && !isRecording && <span className="absolute -inset-1.5 rounded-full border border-warning/50 animate-pulse-soft" aria-hidden />}
              {isRecording ? <CheckIcon /> : <MicIcon />}
            </button>

            <div className="flex items-center justify-end gap-2.5 sm:gap-3">
              <button
                className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer border focus-visible:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
                  paused
                    ? "bg-warning text-cream border-warning shadow-[0_0_0_4px_var(--warning-soft)] hover:brightness-110"
                    : "text-text-secondary border-border-soft hover:text-text-primary hover:border-border"
                }`}
                onClick={onTogglePause}
                disabled={ending}
                title={paused ? "Resume the session" : "Pause the session"}
                aria-label={paused ? "Resume the session" : "Pause the session"}
              >
                {paused ? <PlayIcon /> : <PauseIcon />}
              </button>
              <button
                className={`w-11 h-11 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                  endArmed
                    ? "bg-error text-white border-error shadow-[0_0_0_4px_var(--accent-soft)] animate-pulse-soft"
                    : "text-text-secondary border-border-soft hover:text-error hover:border-error hover:bg-error/10"
                }`}
                onClick={handleEndTap}
                disabled={ending}
                title={endArmed ? "Tap again to end the session" : "End the session"}
                aria-label={endArmed ? "Tap again to end the session" : "End the session"}
              >
                <StopIcon />
              </button>
            </div>
          </div>
        )}
        </div>

        {/* Desktop: mic on the left, typing field always open, controls on the right. */}
        <div className="hidden md:flex items-center gap-3 min-h-[4rem]">
          <button
            className={`relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
              isRecording
                ? "bg-accent text-cream shadow-[0_0_0_6px_var(--accent-soft)]"
                : arming
                  ? "bg-warning text-cream animate-pulse-soft"
                  : "bg-ink text-cream hover:bg-[var(--ink-hover)]"
            }`}
            onClick={onToggleMic}
            disabled={locked}
            title={isRecording ? "Tap when you're done speaking" : arming ? "Opening the mic…" : "Tap to speak"}
            aria-label={isRecording ? "Finish your turn" : arming ? "Opening the mic" : "Start speaking"}
          >
            {isRecording && <span className="absolute -inset-1.5 rounded-full border border-accent/50 animate-pulse-soft" aria-hidden />}
            {arming && !isRecording && <span className="absolute -inset-1.5 rounded-full border border-warning/50 animate-pulse-soft" aria-hidden />}
            {isRecording ? <CheckIcon /> : <MicIcon />}
          </button>

          <input
            ref={desktopInputRef}
            className="input-base flex-1 min-w-0 rounded-full px-4 h-11"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
            disabled={locked || isRecording}
            placeholder={paused ? "Paused" : isRecording ? "Listening…" : "Scrivi in italiano…"}
          />

          <button
            className="w-11 h-11 rounded-full bg-ink text-cream flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer hover:bg-[var(--ink-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={handleSend}
            disabled={!text.trim() || locked || isRecording}
            aria-label="Send message"
          >
            <ArrowUpIcon />
          </button>

          <button
            className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer border focus-visible:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
              paused
                ? "bg-warning text-cream border-warning shadow-[0_0_0_4px_var(--warning-soft)] hover:brightness-110"
                : "text-text-secondary border-border-soft hover:text-text-primary hover:border-border"
            }`}
            onClick={onTogglePause}
            disabled={ending}
            title={paused ? "Resume the session" : "Pause the session"}
            aria-label={paused ? "Resume the session" : "Pause the session"}
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
          </button>

          <button
            className={`w-11 h-11 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
              endArmed
                ? "bg-error text-white border-error shadow-[0_0_0_4px_var(--accent-soft)] animate-pulse-soft"
                : "text-text-secondary border-border-soft hover:text-error hover:border-error hover:bg-error/10"
            }`}
            onClick={handleEndTap}
            disabled={ending}
            title={endArmed ? "Tap again to end the session" : "End the session"}
            aria-label={endArmed ? "Tap again to end the session" : "End the session"}
          >
            <StopIcon />
          </button>
        </div>
      </div>
    </div>
  );
}
